import React, { useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Icon,
  ListGroup,
  ListGroupItem,
  Row,
  toast,
  UncontrolledButtonDropdown,
  useApp
} from 'straightline-ui';
import { DOCUMENT_TYPE } from 'straightline-utils/constants';

const { CARRIER_PACKET, W9, CERTIFICATE_OF_INSURANCE } = DOCUMENT_TYPE;

const documentTypes = [
  { value: CARRIER_PACKET, name: 'Carrier Packet' },
  { value: W9, name: 'W9' },
  { value: CERTIFICATE_OF_INSURANCE, name: 'Insurance Certificate' }
];

const queryKey = {
  query: {
    document_type: {
      $in: [CARRIER_PACKET, W9, CERTIFICATE_OF_INSURANCE]
    },
    $limit: 50,
    $sort: {
      created_at: -1
    }
  }
};

const hasPacketOrDocs = (docs, requiredDocTypes) => {
  if (docs.find((doc) => doc.document_type === CARRIER_PACKET)) {
    return true;
  }

  return requiredDocTypes.every((docType) =>
    docs.find((doc) => doc.document_type === docType)
  );
};

export default function CarrierDocuments() {
  const app = useApp();
  const [documentType, setDocumentType] = useState(null);

  const docsService = app.service('api/documents');

  const inputRef = useRef(null);

  const { result, isLoading, isError, error } = docsService.useFind(queryKey, {
    keepPreviousData: true
  });

  const createDoc = docsService.useCreate();

  const handleClick = (documentType) => {
    setDocumentType(documentType);
    inputRef.current.value = null;
    inputRef.current.click();
  };

  const handleImageChange = (event) => {
    const file = Array.from(event.target.files)[0];
    const type = documentTypes.find(
      (docType) => docType.value === documentType
    );
    const data = {
      file,
      name: type.name,
      document_type: documentType
    };
    createDoc
      .create(data)
      .then(() => {
        setDocumentType(null);
        docsService.invalidateFind(queryKey);
      })
      .catch((err) => toast.errors(err));
  };

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <Error error={error} />;
  }

  const docs = result.data;

  const isComplete = hasPacketOrDocs(result.data, [
    W9,
    CERTIFICATE_OF_INSURANCE
  ]);

  return (
    <Card>
      <div className="px-4 py-3 border-bottom">
        <Row className="align-items-center justify-content-between">
          <Col className="col-auto">
            <UncontrolledButtonDropdown
              className={createDoc.isLoading ? 'is-loading' : ''}
            >
              <DropdownToggle
                caret
                color="white"
                size="sm"
                disabled={createDoc.isLoading}
              >
                Upload
              </DropdownToggle>
              <DropdownMenu left>
                <DropdownItem header>Document Type</DropdownItem>
                {documentTypes.map(({ name, value }) => {
                  return (
                    <DropdownItem
                      key={value}
                      onClick={() => handleClick(value)}
                    >
                      {name}
                    </DropdownItem>
                  );
                })}
                {/* <DropdownItem divider /> */}
              </DropdownMenu>
            </UncontrolledButtonDropdown>
            <input
              type="file"
              accept=".jpg, .png, .pdf, .jpeg"
              className="d-none"
              ref={inputRef}
              onChange={handleImageChange}
            />
          </Col>
          <Col className="col-auto">
            <p className="card-header-title">
              {isComplete ? (
                <Row className="align-items-center">
                  <Col xs="auto" className="pr-0">
                    All required documents uploaded!
                  </Col>
                  <Col xs="auto">
                    <Badge color="success">
                      <Icon type="check-circle" className="mr-2" />
                      Uploaded
                    </Badge>
                  </Col>
                </Row>
              ) : (
                <Row className="align-items-center">
                  <Col xs="auto" className="pr-0">
                    Upload carrier documents
                  </Col>
                  <Col xs="auto">
                    <Badge color="danger">
                      <Icon type="alert-circle" className="mr-2" />
                      Required
                    </Badge>
                  </Col>
                </Row>
              )}
            </p>
          </Col>
        </Row>
      </div>
      <CardBody>
        <ListGroup className="list-group list-group-flush list my-n4">
          {!hasPacketOrDocs(docs, [W9, CERTIFICATE_OF_INSURANCE]) && (
            <UploadItem
              title="Carrier Packet"
              onClick={() => handleClick(CARRIER_PACKET)}
              description="Upload a file containing both a W9 and Certificate of Insurance"
              disabled={createDoc.isLoading}
            />
          )}
          {!hasPacketOrDocs(docs, [W9]) && (
            <UploadItem
              title="W9"
              onClick={() => handleClick(W9)}
              description="W9 is required if not uploading Carrier Packet"
              disabled={createDoc.isLoading}
            />
          )}
          {!hasPacketOrDocs(docs, [CERTIFICATE_OF_INSURANCE]) && (
            <UploadItem
              title="Cert. of Insurance"
              onClick={() => handleClick(CERTIFICATE_OF_INSURANCE)}
              description="Certificate of Insurance is required if not uploading Carrier Packet"
              disabled={createDoc.isLoading}
            />
          )}
          {docs.length > 0 &&
            docs.map((doc) => (
              <ListGroupItem className="px-0" key={doc.id}>
                <Row className="align-items-center">
                  <Col className="col-auto">
                    <a
                      href={doc.secure_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div className="avatar avatar-xl">
                        {doc.format === 'pdf' || doc.format === 'xlsx' ? (
                          <div className="avatar-img rounded bg-light d-flex align-items-center justify-content-center">
                            <Icon type="image" style={{ fontSize: 35 }} />
                          </div>
                        ) : (
                          <img
                            src={doc.secure_url}
                            alt="..."
                            className="avatar-img rounded"
                          />
                        )}
                      </div>
                    </a>
                  </Col>
                  <Col className="ml-n2">
                    <h4 className="card-title mb-1">
                      <a
                        href={doc.secure_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {doc.name}
                      </a>
                    </h4>

                    {/* <p className="small text-muted mb-1">
                        {(doc.bytes / 1000).toFixed(2)} kb
                        .{doc.format}
                      </p> */}

                    <p className="small text-muted mb-1">
                      Type:{' '}
                      {
                        (
                          documentTypes.find(
                            (type) => doc.document_type === type.value
                          ) || {}
                        ).name
                      }
                    </p>
                  </Col>
                </Row>
              </ListGroupItem>
            ))}
        </ListGroup>
      </CardBody>
    </Card>
  );
}

const Loader = () => {
  return (
    <Card>
      <div className="card-header">
        <Row className="align-items-center">
          <Col>
            <p className="card-header-title">Documents</p>
          </Col>
          <Col className="col-auto">
            <Button
              className="dropdown-toggle"
              color="white"
              size="sm"
              disabled={true}
            >
              Upload
            </Button>
          </Col>
        </Row>
      </div>
      <CardBody>
        <ListGroup className="list-group list-group-flush list my-n4">
          <ListGroupItem className="px-0">
            <Row className="align-items-center">
              <Col className="col-auto">
                <div className="avatar avatar-xl">
                  <div className="avatar-img rounded bg-light d-flex align-items-center justify-content-center">
                    <Icon type="image" style={{ fontSize: 35 }} />
                  </div>
                </div>
              </Col>
              <Col className="ml-n2">
                <div
                  className="bg-light rounded mb-2"
                  style={{ height: 18, width: 95 }}
                />
                <div
                  className="bg-light rounded mb-2"
                  style={{ height: 14, width: 75 }}
                />
              </Col>
            </Row>
          </ListGroupItem>
          <ListGroupItem className="px-0">
            <Row className="align-items-center">
              <Col className="col-auto">
                <div className="avatar avatar-xl">
                  <div className="avatar-img rounded bg-light d-flex align-items-center justify-content-center">
                    <Icon type="image" style={{ fontSize: 35 }} />
                  </div>
                </div>
              </Col>
              <Col className="ml-n2">
                <div
                  className="bg-light rounded mb-2"
                  style={{ height: 18, width: 95 }}
                />
                <div
                  className="bg-light rounded mb-2"
                  style={{ height: 14, width: 75 }}
                />
              </Col>
            </Row>
          </ListGroupItem>
        </ListGroup>
      </CardBody>
    </Card>
  );
};

const Error = ({ error }) => {
  return (
    <Card>
      <div className="card-header">
        <Row className="align-items-center">
          <Col>
            <p className="card-header-title">Documents</p>
          </Col>
          <Col className="col-auto">
            <Button
              className="dropdown-toggle"
              color="white"
              size="sm"
              disabled={true}
            >
              Upload
            </Button>
          </Col>
        </Row>
      </div>
      <CardBody>
        <div className="text-center">{error.message}</div>
      </CardBody>
    </Card>
  );
};

const UploadItem = ({ title, onClick, description, disabled }) => {
  return (
    <ListGroupItem className="px-0">
      <Row className="align-items-center">
        <Col className="col-auto">
          <div className="avatar avatar-xl">
            <div className="avatar-img rounded bg-light d-flex align-items-center justify-content-center">
              <Icon type="file-text" style={{ fontSize: 35 }} />
            </div>
          </div>
        </Col>
        <Col className="ml-n2">
          <h3 className="mb-1 text-primary">{title}</h3>
          <p className="small text-muted mb-1">{description}</p>
        </Col>
        <Col className="col-auto">
          <Button
            onClick={onClick}
            size="sm"
            color="primary"
            disabled={disabled}
          >
            Upload
          </Button>
        </Col>
      </Row>
    </ListGroupItem>
  );
};
